import { create } from "zustand";
import {
  getWorkspace,
  renameWorkspace,
  saveAsNewWorkspace,
  saveWorkspace,
} from "../../apis/bacchus/workspace";
import { CompanyFilter } from "../../legacy/types/companyFilter";
import { Market, ViewOption, Workspace } from "../../types/workspace";
import {
  WorkspaceConfigurationSlice,
  createWorkspaceConfigurationSlice,
} from "./slices/workspaceConfigurationSlice";

import {
  WorkspaceICPSlice,
  createWorkspaceICPSlice,
} from "./slices/workspaceICPSlice";
import {
  FilterConflictType,
  hasFilterTypeChanged,
  isRangeDefined,
} from "../../pages/Workspace/utils";
import { FILTER_TYPE_MAP } from "../../pages/Workspace/UserFilters/constants";
export interface WorkspaceState {
  // Persisted workspace (saved in DB)
  persisted: Workspace | undefined;
  // Draft workspace (current state with unsaved changes)
  draft: Workspace | undefined;
  userFilters: CompanyFilter | undefined;
  selectedMarket: Market | undefined;
  selectedView: ViewOption;
  isLoading: boolean;
  isSaving: boolean;
  hasUnsavedChanges: boolean;
  isSamDisabled: boolean;
  isSomDisabled: boolean;
  companyCount: number;
  globalFilterConflictTypes: Set<FilterConflictType>;
  setCompanyCount: (count: number) => void;

  // Actions
  setDraft: (
    workspace: Workspace | ((prev: Workspace | undefined) => Workspace)
  ) => void;
  setSelectedMarket: (market: Market) => void;
  setSelectedView: (view: ViewOption) => void;
  setUserFilters: (filters: CompanyFilter | undefined) => void;
  getIsSamDisabled: () => boolean;
  getIsSomDisabled: () => boolean;

  // Async actions
  fetchWorkspace: (id: string) => Promise<void>;
  saveWorkspace: () => Promise<void>;
  saveAsNewWorkspace: (
    successCallback: (createdWorkspace: Workspace) => Promise<void>
  ) => void;
  renameWorkspace: (name: string) => Promise<void>;
  discardChanges: () => void;
  detectModifiedFilterTypes: (
    oldFilters: Workspace | undefined,
    newFilters: Workspace | undefined
  ) => void;
}

// Combined state type with all slices
type WorkspaceStateWithSlices = WorkspaceState &
  WorkspaceConfigurationSlice &
  WorkspaceICPSlice;

const marketTab = [Market.TAM, Market.SAM, Market.SOM];

export const useWorkspaceStore = create<WorkspaceStateWithSlices>(
  (set, get, api) => ({
    // Merge all slices into the main state
    ...createWorkspaceConfigurationSlice(set, get, api),
    ...createWorkspaceICPSlice(set, get, api),
    companyCount: 0,

    persisted: undefined,
    draft: undefined,
    userFilters: undefined,
    selectedMarket: undefined,
    selectedView: ViewOption.MARKET_LANDSCAPE,
    isLoading: false,
    isSaving: false,
    hasUnsavedChanges: false,
    isSamDisabled: false,
    isSomDisabled: false,
    globalFilterConflictTypes: new Set<FilterConflictType>(),

    setCompanyCount: (count) => {
      set({ companyCount: count });
    },

    setDraft: (workspaceOrFn) => {
      set((state) => {
        const { draft, detectModifiedFilterTypes } = get();
        const newDraft =
          typeof workspaceOrFn === "function"
            ? workspaceOrFn(state.draft)
            : workspaceOrFn;

        // Compare draft with persisted to determine if there are unsaved changes
        const hasUnsavedChanges =
          JSON.stringify(newDraft) !== JSON.stringify(state.persisted);

        // Detect which filter types have changed
        if (hasUnsavedChanges) {
          detectModifiedFilterTypes(draft, newDraft);
        }

        return {
          draft: newDraft,
          hasUnsavedChanges,
        };
      });
    },
    getIsSamDisabled: () => {
      const workspace = get().draft;

      const values = [
        workspace?.configuration.sam.industry.ids,
        workspace?.configuration.sam.industry.sicIds,
        workspace?.configuration.sam.industry.naicsIds,
        workspace?.configuration.sam.industry.excludedIds,
        workspace?.configuration.sam.industry.excludedSicIds,
        workspace?.configuration.sam.industry.excludedNaicsIds,
        workspace?.configuration.sam.geography.countryIds,
        workspace?.configuration.sam.geography.regionsIds,
        workspace?.configuration.sam.geography.stateIds,
        workspace?.configuration.sam.geography.excludedCountryIds,
      ];

      return values.flat().length === 0;
    },
    getIsSomDisabled: () => {
      const workspace = get().draft;
      const employeeRange = workspace?.configuration.som.employeeRange;
      const revenueRange = workspace?.configuration.som.revenueRange;
      const productIds = workspace?.configuration.som.productIds;
      const excludedProductIds =
        workspace?.configuration.som.excludedProductIds;

      const isEmployeeRangeInvalid =
        !employeeRange || !isRangeDefined(employeeRange);

      const isRevenueRangeInvalid =
        !revenueRange || !isRangeDefined(revenueRange);

      const isProductIdsInvalid = !productIds || productIds.length === 0;

      const isExcludedProductIdsInvalid =
        !excludedProductIds || excludedProductIds.length === 0;

      return (
        isEmployeeRangeInvalid &&
        isRevenueRangeInvalid &&
        isProductIdsInvalid &&
        isExcludedProductIdsInvalid
      );
    },
    setSelectedMarket: (market) =>
      set({
        selectedMarket: market,
        activeConfigTab: marketTab.indexOf(market),
      }),
    setSelectedView: (view) => set({ selectedView: view }),

    setUserFilters: (filters) => {
      set((state) => {
        if (!state.draft) return state;

        const hasUnsavedChanges =
          state.hasUnsavedChanges ||
          JSON.stringify(filters) !== JSON.stringify(state.userFilters || {});

        const newDraft = {
          ...state.draft,
          userFilters: filters,
        };

        return {
          draft: newDraft,
          userFilters: filters, // Update the userFilters at the root level too
          hasUnsavedChanges,
        };
      });
    },

    fetchWorkspace: async (id) => {
      set({ isLoading: true });
      try {
        const workspace = await getWorkspace(id);
        const userFilters = JSON.parse(
          (workspace.filters as string) || "{}"
        ) as CompanyFilter;

        set({
          persisted: workspace,
          draft: workspace,
          hasUnsavedChanges: false,
          isLoading: false,
          userFilters,
        });

        // Load ICP data from the workspace after it's been set
        const state = get();
        await state.loadICPFromWorkspace();
      } catch (error) {
        console.error("Failed to fetch workspace", error);
        set({ isLoading: false });
      }
    },

    saveWorkspace: async () => {
      const { draft, persisted, userFilters } = get();
      if (!draft || !persisted) return;

      set({ isSaving: true });

      try {
        // Only send the configuration
        const workspaceData = {
          configuration: draft.configuration,
          filters: JSON.stringify(draft.userFilters || {}),
        };

        const result = await saveWorkspace(draft.id, workspaceData);

        if (result.error) {
          throw new Error(result.error.message);
        }

        const updatedWorkspaceWithFilters = {
          ...result.data,
          userFilters, // Preserve the current filters
        };

        set({
          persisted: updatedWorkspaceWithFilters,
          draft: updatedWorkspaceWithFilters,
          userFilters,
          hasUnsavedChanges: false,
          isSaving: false,
        });
      } catch (error) {
        console.error("Failed to save workspace", error);
        set({ isSaving: false });
      }
    },

    saveAsNewWorkspace: async (
      successCallback: (createdWorkspace: Workspace) => Promise<void>
    ) => {
      const { draft } = get();
      if (!draft) return;

      set({ isSaving: true });

      const params = {
        configuration: draft.configuration,
        filters: JSON.stringify(draft.userFilters || {}),
      };

      const result = await saveAsNewWorkspace(draft.id, params);
      const createdWorkspace = result.data as Workspace;

      set({ persisted: createdWorkspace, draft: createdWorkspace });
      await successCallback(createdWorkspace);
      set({ isSaving: false, hasUnsavedChanges: false });
    },

    renameWorkspace: async (newName: string) => {
      const { draft, persisted } = get();
      if (!draft || !persisted) return;

      set({ isSaving: true });

      try {
        const result = await renameWorkspace(draft.id, newName);

        if (result.error && result.error.message) {
          throw new Error(result.error.message);
        }

        const updatedWorkspace = result.data as Workspace;

        set({
          persisted: updatedWorkspace,
          draft: updatedWorkspace,
          isSaving: false,
        });
      } catch (error) {
        set({ isSaving: false });
        throw error;
      }
    },

    discardChanges: () => {
      const { persisted } = get();

      if (persisted) {
        set({
          draft: persisted,
          hasUnsavedChanges: false,
        });

        // Reload ICP data from the persisted workspace
        const state = get();
        state.loadICPFromWorkspace();
      }
    },

    detectModifiedFilterTypes: (oldFilters, newFilters) => {
      if (!oldFilters && !newFilters) {
        set((state) => ({
          ...state,
          globalFilterConflictTypes: new Set(),
        }));
        return;
      }
      const allTypes = Object.values(FILTER_TYPE_MAP);
      const currentModifiedTypes = get().globalFilterConflictTypes;

      allTypes.forEach((type) => {
        if (hasFilterTypeChanged(type, oldFilters, newFilters)) {
          currentModifiedTypes.add(type);
        }
      });
      set((state) => ({
        ...state,
        globalFilterConflictTypes: currentModifiedTypes,
      }));
    },
  })
);
